/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, {
  PADDING_HORIZONTAL_SQUARE,
  PADDING_VERTICAL,
} from './WireframeBase';
import { FlexRow, type FlexRowProps } from '@lib/canvasx/components/FlexRow';
import tools from '../tools';
import {
  FlexColumn,
  type FlexColumnProps,
} from '@lib/canvasx/components/FlexColumn';
import { TextWithIcons } from '../components/wireframes/TextWithIcons';
import WireframeLinearResizeControl from '../controls/WireframeLinearResizeControl';
import { sumBy } from 'lodash';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type WireframeItem from './WireframeItem';
import {
  WIREFRAME_SELECTION_COLOR,
  WIREFRAME_PRIMARY_COLOR,
} from '../utils/wireframes';
import WireframeAddItemControl from '../controls/WireframeAddItemControl';

const DEFAULT_ITEM_TEXT = 'Menu Item';
const SEPARATOR_SIZE = 1;

class WireframeNavMenuBase extends WireframeBase {
  hasDraggableItems = true;

  constructor(fabricObject) {
    super(fabricObject);
    // Backwards compat: added the "flag" prefix to the style keys
    if (this.fabricObject.icons) {
      this.fabricObject.set('showIcons', this.fabricObject.icons);
      this.fabricObject.set('icons', null);
    }
    if (this.fabricObject.separators) {
      this.fabricObject.set('showSeparators', this.fabricObject.separators);
      this.fabricObject.set('separators', null);
    }

    this.fabricObject.on('selected', () => this.showAddItemControl());
  }

  initItems() {
    if (this.fabricObject.items.items.length === 0) {
      this.fabricObject.items.add({ value: DEFAULT_ITEM_TEXT });
      this.fabricObject.items.add({
        value: DEFAULT_ITEM_TEXT,
        state: 'selected',
      });
      this.fabricObject.items.add({ value: DEFAULT_ITEM_TEXT });
    }
  }

  static externalName(): string {
    return 'Tabs';
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      ml: new WireframeLinearResizeControl({ edge: 'left' }),
      mr: new WireframeLinearResizeControl({ edge: 'right' }),
    };
  }

  showAddItemControl() {
    // use `componentLayout` to be consistent with other wireframes, even though
    // this wireframe has the 'layout' field
    const layout = this.componentLayout;
    const itemInRow = layout === 'row';
    const key = `add_${itemInRow ? 'rowItem' : 'columnItem'}`;
    const otherControlKey = `add_${itemInRow ? 'columnItem' : 'rowItem'}`;

    // Create add control if it doesn't exist yet for this layout
    if (!this.fabricObject.controls[key]) {
      this.fabricObject.controls[key] = new WireframeAddItemControl({
        layout,
      });
    }

    // Hide add control for opposite layout
    if (this.fabricObject.controls[otherControlKey]) {
      this.fabricObject.controls[otherControlKey].setVisibility(false);
    }
    // Show add control for this layout
    this.fabricObject.controls[key].setVisibility(true);
  }

  minDimension() {
    return this.fabricObject.layout === 'row'
      ? this.childrenWidth
      : this.maxItemDimensions.width;
  }

  get componentLayout() {
    return this.fabricObject.layout;
  }

  get requiresPriorSelection() {
    return true;
  }

  get childrenWidth() {
    const { items } = this.fabricObject;
    return this.componentLayout === 'row'
      ? sumBy(
          items.items,
          (item: WireframeItem) => item.dimensions?.unwrappedWidth ?? 0
        ) +
          SEPARATOR_SIZE * (items.items.length - 1)
      : this.maxItemDimensions.width;
  }

  get itemSeparatorSize() {
    return SEPARATOR_SIZE;
  }

  tools() {
    // Show tools for editing individual nav menu items
    if (this.activeKey) {
      const itemTools = [
        [tools.ToolWireframeItemState],
        [tools.ToolWireframeIcon],
      ];

      // Show delete tool only if more than two items left
      if (this.fabricObject.items.items.length > 2) {
        itemTools.push([tools.ToolWireframeDeleteItem]);
      }

      return itemTools;
    }

    return [
      [tools.ToolWireframeAddItem],
      [tools.ToolWireframeLayout],
      [tools.ToolNavMenuStyleFlags],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
      [tools.ToolWireframeTextColor],
    ];
  }

  defaultText() {
    return 'Menu Item';
  }

  supportedIcons(): string[] {
    return ['iconLeft'];
  }

  setLayout(layout: 'row' | 'column') {
    const isRow = layout === 'row';
    const wireframeName = isRow ? 'navMenuHorizontal' : 'navMenuVertical';

    // Update the dimensiosn based on the sum of items
    const { items } = this.fabricObject;
    const height = isRow
      ? this.maxItemDimensions.height
      : sumBy(items.items, (item: WireframeItem) => item.dimensions?.height);
    const width = isRow ? this.childrenWidth : this.maxItemDimensions.width;

    this.fabricObject.set({
      layout,
      height,
      width,
      wireframeName,
    });
    // Avoid leaving behind stale item drag control
    this.fabricObject.hideItemDragControls();
    // Update position of add item control
    this.showAddItemControl();
    this.updateDimensions = true;

    // When the AI is drawing the wireframe, it might set the layout before the item is placed.
    if (this.fabricObject.canvas) {
      this.fabricObject.canvas.requestRenderAll();
    }
    // Layout may shift object slightly
    this.fabricObject.controller.actionRerenderToolbar();
  }

  wasSet(keyOrPath: KeyPath, value: any) {
    super.wasSet(keyOrPath, value);
    if (keyOrPath === 'layout') {
      this.setLayout(value);
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const {
      width,
      scaleX,
      layout,
      items,
      textEditing,
      showIcons,
      showSeparators,
      textColor = 'black',
      fontSize,
    } = this.fabricObject;
    const userSetDimensions =
      this.fabricObject.userHasExplicitlySetDimensions();
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    let renderedDimensions: DimensionStruct;
    const isRow = layout === 'row';

    const FlexElement = isRow ? FlexRow : FlexColumn;

    const flexProps = {};
    if (!isRow) {
      (flexProps as FlexColumnProps).horizontalAlignment = 'stretch';
    }

    // Use explicit dimension if user dragged to resize
    if (userSetDimensions) {
      (flexProps as FlexColumnProps & FlexRowProps).width = width;
    }

    // For vertical menu, use max item width if no explicit width is set
    if (!isRow && !userSetDimensions) {
      (flexProps as FlexColumnProps & FlexRowProps).width = Math.max(
        width,
        this.maxItemDimensions.width
      );
    }

    // Allow component to shrink when children shrink
    this.resetMaxItemDimensions();

    const side = isRow ? 'bottom' : 'left';

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexElement
            separator={{
              lineWidth: SEPARATOR_SIZE,
              color: showSeparators ? fill : 'transparent',
              direction: isRow ? 'vertical' : 'horizontal',
              opacity: 0.28,
            }}
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
            {...flexProps}
          >
            {items.items.map(item => {
              const { id, state, iconLeft, dimensions } = item;
              let value: string | null = item.value;
              const active = this.activeKey.includes(id);
              const selected = state === 'selected';

              const editingItem = textEditing && this.activeItemId() === id;
              const icon = showIcons ? iconLeft : undefined;
              const width = dimensions?.unwrappedWidth ?? 0;
              const height = dimensions?.height ?? 0;
              const selectedBorder: BorderStruct = {
                [side]: fill,
                opacity: 1,
                lineWidth: 2,
                size: isRow
                  ? width - PADDING_HORIZONTAL_SQUARE * 2 // underling text
                  : height - PADDING_VERTICAL, // slight padding around text
              };

              // Allow it to render with just the icon if the text is empty and it's not the active item
              if (value === '' && this.activeItemId() !== id && icon) {
                value = null;
              }

              return (
                <FlexColumn
                  key={id}
                  border={active ? WIREFRAME_SELECTION_COLOR : 'transparent'}
                  fill={{
                    color: selected ? fill : 'transparent',
                    opacity: selected ? 0.05 : 1,
                  }}
                  horizontalAlignment={isRow ? 'center' : 'left'}
                  flexGrow
                  setRenderedDimensions={dimensions => {
                    item.containerDimensions = dimensions;
                  }}
                >
                  <TextWithIcons
                    textKey={`text:${id}`}
                    padding={{
                      horizontal: PADDING_HORIZONTAL_SQUARE,
                      vertical: PADDING_VERTICAL,
                    }}
                    border={selected ? selectedBorder : 'transparent'}
                    cursor='pointer'
                    textEditing={editingItem}
                    iconLeft={icon}
                    color={selected ? fill : textColor}
                    iconColor={fill}
                    size={fontSize}
                    onChange={value => {
                      this.onItemChange(value);
                    }}
                    setRenderedDimensions={dimensions => {
                      this.updateMaxItemDimensions({
                        dimensions,
                        useUnwrappedWidth: true,
                      });
                      item.dimensions = dimensions;
                    }}
                  >
                    {value}
                  </TextWithIcons>
                </FlexColumn>
              );
            })}
          </FlexElement>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframeNavMenuBase;
